import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Swal from "sweetalert2";
import { getSessionStorage } from "../../Helper";
import { API_BASE_URL } from "../../Config";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDAvatar from "components/MDAvatar";
import MDProgress from "components/MDProgress";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

// @mui icons
import Icon from "@mui/material/Icon";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CachedIcon from "@mui/icons-material/Cached";
import ErrorIcon from "@mui/icons-material/Error";

// Modal components
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Badge from "@mui/material/Badge";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import { Typography, useTheme } from "@mui/material";

const CNoteTracking = () => {
  const showSidebar = useSelector((state) => state.sidebar.showSidebar);
  const [docket, setDocket] = useState("");
  const [docketError, setDocketError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [deliveryData, setDeliveryData] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [statusDetails, setStatusDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [tabValue, setTabValue] = useState("1");

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDocketChange = (event) => {
    const newDocket = event.target.value;
    setDocket(newDocket);
    if (!newDocket.trim()) {
      setDocketError("CNote number is required");
    } else {
      setDocketError("");
    }
  };

  const handleCheckDocket = async () => {
    if (!docket.trim()) {
      setDocketError("CNote number is required");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/Cnote/track`, {
        params: {
          cid: getSessionStorage("cid"),
          accode: getSessionStorage("accode"),
          cNoteNo: docket,
        },
      });

      if (response.status === 200 && response.data.length > 0) {
        setDeliveryData(response.data);
      } else {
        Swal.fire({
          icon: "error",
          title: "No Records Found",
          text: "No tracking data available for this C-Note number.",
        });
      }
    } catch (error) {
      console.error("Error fetching delivery data:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to Load Tracking Data",
        text: "Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEnterKeyPress = (event) => {
    if (event.key === "Enter") {
      handleCheckDocket();
    }
  };

  const fetchStatusDetails = async (cNoteNo) => {
    setStatusLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/Cnote/trackingStatus`, {
        params: {
          cid: getSessionStorage("cid"),
          cNoteNo: cNoteNo,
        },
      });

      if (response.status === 200 && response.data.length > 0) {
        setStatusDetails(response.data);
      } else {
        setStatusDetails([]);
        Swal.fire({
          icon: "info",
          title: "No Status Details",
          text: "No status updates available for this C-Note.",
        });
      }
    } catch (error) {
      console.error("Error fetching status details:", error);
      setStatusDetails([]);
      Swal.fire({
        icon: "error",
        title: "Failed to Load Status Details",
        text: "Please try again later.",
      });
    } finally {
      setStatusLoading(false);
    }
  };

  const handleViewClick = (item) => {
    setSelectedDelivery(item);
    setShowModal(true);
    fetchStatusDetails(item.CNOTENO);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDelivery(null);
    setStatusDetails([]);
    setTabValue("1");
  };

  // DataTable columns
  const columns = [
    {
      Header: () => (
        <MDTypography variant="h6" fontWeight="bold" align="center">
          CNote No
        </MDTypography>
      ),
      accessor: "cnoteNo",
      align: "center",
      width: "15%",
    },
    {
      Header: () => (
        <MDTypography variant="h6" fontWeight="bold" align="center">
          Pickup Date
        </MDTypography>
      ),
      accessor: "pickupDate",
      align: "center",
      width: "15%",
    },
    {
      Header: () => (
        <MDTypography variant="h6" fontWeight="bold" align="center">
          Origin
        </MDTypography>
      ),
      accessor: "origin",
      align: "center",
      width: "20%",
    },
    {
      Header: () => (
        <MDTypography variant="h6" fontWeight="bold" align="center">
          Destination
        </MDTypography>
      ),
      accessor: "destination",
      align: "center",
      width: "20%",
    },
    {
      Header: () => (
        <MDTypography variant="h6" fontWeight="bold" align="center">
          Status
        </MDTypography>
      ),
      accessor: "status",
      align: "center",
      width: "15%",
    },
    {
      Header: () => (
        <MDTypography variant="h6" fontWeight="bold" align="center">
          Actions
        </MDTypography>
      ),
      accessor: "actions",
      align: "center",
      width: "15%",
    },
  ];

  // DataTable rows
  const rows = deliveryData.map((item) => ({
    cnoteNo: (
      <MDTypography variant="caption" fontWeight="medium">
        {item.CNOTENO}
      </MDTypography>
    ),
    pickupDate: (
      <MDTypography variant="caption" fontWeight="medium">
        {item.CNOTEDATE}
      </MDTypography>
    ),
    origin: (
      <MDTypography variant="caption" fontWeight="medium">
        {item.ORIGIN}
      </MDTypography>
    ),
    destination: (
      <MDTypography variant="caption" fontWeight="medium">
        {item.DESTINATION}
      </MDTypography>
    ),
    status: (
      <MDTypography variant="caption" fontWeight="medium">
        {item.CURRENTSTATUS}
      </MDTypography>
    ),
    actions: (
      <MDButton
        variant="outlined"
        color="info"
        size="small"
        onClick={() => handleViewClick(item)}
        startIcon={<VisibilityIcon />}
      >
        View
      </MDButton>
    ),
  }));

  // Modal style
  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "95%", sm: "90%", md: "85%", lg: "70%" },
    maxWidth: "1000px",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 2,
    p: { xs: 2, sm: 3, md: 4 },
    maxHeight: "90vh",
    overflowY: "auto",
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card sx={{ p: 2, boxShadow: 3, marginTop: 3 }}>
        <MDBox
          className={`mt-[24%] sm:mt-0 md:mt-0 lg:mt-0 2xl:mt-0 h-full ${
            showSidebar
              ? "transition-all duration-500 ease-in-out lg:ml-[18%]"
              : "transition-all duration-500 ease-in-out"
          }`}
        >
          <MDBox
            // ml={2}
            // mr={2}
            // mb={2}
            // mt={4}
            // px={4}
            py={4}
            // shadow="lg"
            borderRadius="lg"
            // bgColor="white"
          >
            <MDBox display="flex" flexDirection="column" width="100%">
              <MDTypography variant="caption" color="text" fontWeight="medium" mb={1}>
                Enter C-Note Number <span style={{ color: "red" }}>*</span> &nbsp;&nbsp;&nbsp;
                <span style={{ color: "gray", fontSize: "0.75rem" }}>
                  (For multiple dockets, enter comma-separated values like: 123456, 123457, 123458)
                </span>
              </MDTypography>

              <MDBox
                display="flex"
                flexDirection={{ xs: "column", lg: "row" }}
                gap={2}
                width="100%"
              >
                <MDBox flexGrow={1} position="relative">
                  <MDInput
                    type="text"
                    fullWidth
                    placeholder="Enter Docket/C-Note Number"
                    value={docket}
                    onChange={handleDocketChange}
                    onKeyDown={handleEnterKeyPress}
                    error={!!docketError}
                  />
                  {docketError && (
                    <ErrorIcon
                      color="error"
                      sx={{
                        position: "absolute",
                        right: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    />
                  )}
                </MDBox>

                <MDButton
                  variant="outlined"
                  color="info"
                  onClick={handleCheckDocket}
                  disabled={!docket || docketError || loading}
                  startIcon={loading ? <CircularProgress size={20} color="info" /> : <CachedIcon />}
                >
                  {loading ? "Loading..." : "Check Docket"}
                </MDButton>
              </MDBox>
              {docketError && (
                <MDTypography variant="caption" color="error">
                  {docketError}
                </MDTypography>
              )}
            </MDBox>
          </MDBox>

          {deliveryData.length > 0 && (
            <MDBox mt={2} px={2} width="100%" sx={{ overflowX: "auto" }}>
              <Card>
                <MDBox p={3}>
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                    sx={{
                      "& .MuiTableCell-root": {
                        fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.875rem" },
                        padding: { xs: "8px", sm: "12px" },
                      },
                    }}
                  />
                </MDBox>
              </Card>
            </MDBox>
          )}

          {/* Modal */}
          <Modal
            open={showModal}
            onClose={handleCloseModal}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <Box sx={modalStyle}>
              <MDTypography
                variant="h5"
                component="h2"
                mb={3}
                sx={{ color: isDark ? "##ffffff" : "#121212" }}
              >
                C-Note Tracking Details
              </MDTypography>

              {selectedDelivery && (
                <Box>
                  <Card>
                    <CardHeader
                      title="Delivery Details"
                      sx={{
                        backgroundColor: "#D3E3FD",
                        color: "#666666",
                        "& .MuiCardHeader-title": {
                          fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                        },
                      }}
                    />
                    <CardContent>
                      <MDBox mb={4}>
                        <MDBox
                          display="flex"
                          flexDirection={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          gap={2}
                          mb={2}
                        >
                          <MDTypography variant="body2" fontWeight="medium">
                            Waybill No:{" "}
                            <span style={{ fontWeight: "normal" }}>{selectedDelivery.CNOTENO}</span>
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium">
                            Pickup Date:{" "}
                            <span style={{ fontWeight: "normal" }}>
                              {selectedDelivery.CNOTEDATE}
                            </span>
                          </MDTypography>
                        </MDBox>

                        <MDBox
                          display="flex"
                          flexDirection={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          gap={2}
                          mb={2}
                        >
                          <MDTypography variant="body2" fontWeight="medium">
                            From:{" "}
                            <span style={{ fontWeight: "normal" }}>{selectedDelivery.ORIGIN}</span>
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium">
                            To:{" "}
                            <span style={{ fontWeight: "normal" }}>
                              {selectedDelivery.DESTINATION}
                            </span>
                          </MDTypography>
                        </MDBox>

                        <MDBox
                          display="flex"
                          flexDirection={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          gap={2}
                          mb={2}
                        >
                          <MDTypography variant="body2" fontWeight="medium">
                            Expected Date of Delivery:{" "}
                            <span style={{ fontWeight: "normal" }}>{selectedDelivery.EDDATE}</span>
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="medium">
                            Status:{" "}
                            <span style={{ fontWeight: "normal" }}>
                              {selectedDelivery.CURRENTSTATUS}
                            </span>
                          </MDTypography>
                        </MDBox>
                      </MDBox>

                      <TabContext value={tabValue}>
                        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                          <TabList
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                          >
                            <Tab label="Delivery Details" value="1" />
                            <Tab label="Status and Scans" value="2" />
                          </TabList>
                        </Box>
                        <TabPanel value="1" sx={{ p: { xs: 0, sm: 1, md: 2 } }}>
                          <TableContainer
                            component={Paper}
                            sx={{ maxWidth: "100%", overflowX: "auto" }}
                          >
                            <Table size="small" sx={{ minWidth: 650 }}>
                              <TableBody sx={{ backgroundColor: isDark ? "#121212" : "#ffffff" }}>
                                {[
                                  { label: "Date of Delivery", value: selectedDelivery.ADDATE },
                                  { label: "No of Packages", value: selectedDelivery.NOPACKAGES },
                                  { label: "Consignor", value: selectedDelivery.CONSIGNOR },
                                  { label: "Consignee", value: selectedDelivery.CONSIGNEE },
                                  {
                                    label: "Charged Weight",
                                    value: `${selectedDelivery.CHARGABLEWEIGHT} kg`,
                                  },
                                  { label: "MBA Ref No", value: selectedDelivery.COMPREFNO },
                                  {
                                    label: "POD File",
                                    value: selectedDelivery.PODFILE ? (
                                      <a
                                        href={`https://www.magixerp.in/ImageView/MAXPACIFIC/POD/${selectedDelivery.PODFILE}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        View POD File
                                      </a>
                                    ) : (
                                      "N/A"
                                    ),
                                  },
                                ].map((row, index) => (
                                  <TableRow key={index}>
                                    <TableCell
                                      sx={{
                                        fontWeight: "bold",
                                        width: "30%",
                                        fontSize: {
                                          xs: "0.75rem",
                                          sm: "0.8125rem",
                                          md: "0.875rem",
                                        },
                                      }}
                                    >
                                      {row.label}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        fontWeight: "bold",
                                        fontSize: {
                                          xs: "0.75rem",
                                          sm: "0.8125rem",
                                          md: "0.875rem",
                                        },
                                      }}
                                    >
                                      {row.value}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </TabPanel>
                        <TabPanel value="2" sx={{ p: 2 }}>
                          <TableContainer
                            component={Paper}
                            sx={{ maxHeight: "400px", maxWidth: "100%", overflow: "auto" }}
                          >
                            <Table sx={{ backgroundColor: isDark ? "#121212" : "#ffffff" }}>
                              <TableRow sx={{ width: "100%" }}>
                                <TableCell
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: "#f5f5f5",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 1,
                                    width: "20.33%", // Fixed width for Status column
                                    minWidth: "120px", // Minimum width to ensure readability
                                    padding: "8px",
                                    textAlign: "left",
                                  }}
                                >
                                  Status
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: "#f5f5f5",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 1,
                                    width: "33.33%", // Fixed width for Scan Date column
                                    minWidth: "120px", // Minimum width to ensure readability
                                    padding: "8px",
                                    textAlign: "left",
                                  }}
                                >
                                  Scan Date
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: "bold",
                                    backgroundColor: "#f5f5f5",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 1,
                                    width: "33.33%", // Fixed width for Scan Time column
                                    minWidth: "120px", // Minimum width to ensure readability
                                    padding: "8px",
                                    textAlign: "left",
                                  }}
                                >
                                  Scan Time
                                </TableCell>
                              </TableRow>

                              <TableBody>
                                {statusLoading ? (
                                  <TableRow width="100%">
                                    <TableCell colSpan={3} align="center">
                                      <CircularProgress color="info" />
                                      <Typography variant="body2" mt={1}>
                                        Loading status details...
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ) : statusDetails.length > 0 ? (
                                  statusDetails.map((status, index) => (
                                    <TableRow key={index}>
                                      <TableCell
                                        sx={{
                                          width: "33.33%", // Matches header width
                                          minWidth: "120px", // Matches header minWidth
                                          padding: "8px",
                                          textAlign: "left",
                                        }}
                                      >
                                        {status.CURRENTSTATUS || "N/A"}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          width: "33.33%", // Matches header width
                                          minWidth: "120px", // Matches header minWidth
                                          padding: "8px",
                                          textAlign: "left",
                                        }}
                                      >
                                        {status.SCANDATE || "N/A"}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          width: "33.33%", // Matches header width
                                          minWidth: "120px", // Matches header minWidth
                                          padding: "8px",
                                          textAlign: "left",
                                        }}
                                      >
                                        {status.SCANTIME || "N/A"}
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <TableRow>
                                    <TableCell colSpan={3} align="center">
                                      No Status Details Available
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </TabPanel>
                      </TabContext>
                    </CardContent>
                  </Card>
                </Box>
              )}

              <MDBox mt={3} display="flex" justifyContent="flex-end">
                <MDButton variant="contained" color="error" onClick={handleCloseModal}>
                  Close
                </MDButton>
              </MDBox>
            </Box>
          </Modal>
        </MDBox>
      </Card>
    </DashboardLayout>
  );
};

export default CNoteTracking;
